import { useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/atlas/Shared";
import { Field, GoldButton, NativeSelect } from "@/components/atlas/Fields";
import { api } from "@/lib/api";
import { useFetch, usePositions } from "@/lib/positions";
import { useLang, errorText } from "@/lib/i18n";

const CATS = ["hospitality", "kitchen", "operations", "custom"];

export default function Settings() {
  const { t } = useLang();
  const { positions, label, reload } = usePositions();
  const { data: templates, reload: reloadTpl } = useFetch("/templates");
  const [np, setNp] = useState({ fr: "", en: "", category: "custom" });

  const addPosition = async (e) => {
    e.preventDefault();
    try {
      await api.post("/positions", np);
      setNp({ fr: "", en: "", category: "custom" });
      reload();
      toast.success(t("c.saved"));
    } catch (err) {
      toast.error(errorText(t, err));
    }
  };
  const delTpl = async (id) => {
    await api.delete(`/templates/${id}`);
    reloadTpl();
  };

  return (
    <div>
      <PageHeader eyebrow="Atlas" title={t("set.title")} />
      <div className="grid xl:grid-cols-[1.4fr_1fr] gap-8">
        <section className="card-lux p-6 md:p-8">
          <h2 className="font-display text-3xl mb-6">{t("set.positions")}</h2>
          <div className="grid sm:grid-cols-2 gap-6" data-testid="positions-list">
            {CATS.map((c) => {
              const list = positions.filter((p) => p.category === c);
              if (!list.length) return null;
              return (
                <div key={c}>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#8a6d2f] mb-2">{t(`cat.${c}`)}</div>
                  <ul className="space-y-1">{list.map((p) => <li key={p.key} className="text-sm border-b border-black/[0.04] py-1.5" data-testid={`position-item-${p.key}`}>{label(p.key)}</li>)}</ul>
                </div>
              );
            })}
          </div>
          <form onSubmit={addPosition} className="mt-8 pt-6 border-t border-black/[0.06] grid sm:grid-cols-[1fr_1fr_160px_auto] gap-3 items-end" data-testid="add-position-form">
            <Field label={t("set.fr")}><Input data-testid="new-position-fr" required value={np.fr} onChange={(e) => setNp({ ...np, fr: e.target.value })} className="h-10 rounded-none" /></Field>
            <Field label={t("set.en")}><Input data-testid="new-position-en" required value={np.en} onChange={(e) => setNp({ ...np, en: e.target.value })} className="h-10 rounded-none" /></Field>
            <Field label={t("set.category")}>
              <NativeSelect testid="new-position-category" value={np.category} onChange={(v) => setNp({ ...np, category: v })}>
                {CATS.map((c) => <option key={c} value={c}>{t(`cat.${c}`)}</option>)}
              </NativeSelect>
            </Field>
            <GoldButton data-testid="new-position-submit"><Plus className="w-4 h-4" />{t("c.add")}</GoldButton>
          </form>
        </section>
        <section className="card-lux p-6 md:p-8 self-start">
          <h2 className="font-display text-3xl mb-2">{t("set.templates")}</h2>
          <p className="text-xs text-neutral-500 mb-6">{t("set.tplHint")}</p>
          <div className="space-y-2" data-testid="templates-list">
            {templates?.length === 0 && <p className="text-sm text-neutral-400">{t("set.noTemplates")}</p>}
            {(templates || []).map((tp) => (
              <div key={tp.id} className="flex items-center justify-between border border-black/[0.07] p-3" data-testid={`template-${tp.id}`}>
                <div>
                  <div className="text-sm font-medium">{tp.name}</div>
                  <div className="text-xs text-neutral-500">{tp.data.positions.length} {t("c.positions").toLowerCase()} · {tp.data.positions.reduce((s, p) => s + p.required, 0)} pers.</div>
                </div>
                <button data-testid={`delete-template-${tp.id}`} onClick={() => delTpl(tp.id)} className="press p-2 text-neutral-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
